// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Component, ChangeDetectionStrategy, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HoverTriggerComponent } from '../hover-trigger/hover-trigger.component';

/**
 * Widget action item configuration
 * 
 * Defines a single action that appears in the widget menu.
 * Actions can use either FontAwesome icons or SVG assets.
 * 
 * @example
 * ```typescript
 * const shareAction: WidgetAction = {
 *   id: 'share',
 *   tooltip: 'Share',
 *   icon: 'fas fa-share-alt',
 *   ariaLabel: 'Share this widget'
 * };
 * ```
 */
export interface WidgetAction {
  /** Unique identifier for the action */
  id: string;
  
  /** Tooltip text displayed on hover */
  tooltip: string;
  
  /** FontAwesome icon class (e.g., 'fas fa-share') */
  icon?: string;
  
  /** SVG asset path (e.g., 'assets/icons/share.svg') */
  svgIcon?: string;
  
  /** Accessibility label for screen readers */
  ariaLabel?: string;
  
  /** Whether the action is disabled */
  disabled?: boolean;
}

/**
 * Event emitted when a widget action is clicked
 */
export interface WidgetActionClickEvent {
  /** The action that was clicked */
  action: WidgetAction;
  
  /** The ID of the action (for convenience) */
  actionId: string;
}

/**
 * Menu positioning options
 */
export type MenuPosition = 'top-left' | 'top-right' | 'top-center';

/**
 * Widget Menu Component
 * 
 * A reusable, pill-shaped action menu that appears on hover over parent components.
 * Designed for use with cards, widgets, and any component needing contextual actions.
 * 
 * ## Features
 * - Pill-shaped design with circular icon buttons
 * - Configurable positioning (top-left, top-right, top-center)
 * - Supports both FontAwesome and SVG icons
 * - Tooltips for each action
 * - Smooth fade-in/fade-out transitions
 * - Always visible on mobile devices
 * - Accessibility support with ARIA labels
 * 
 * ## Usage
 * 
 * ### Basic Example
 * ```html
 * <div class="card">
 *   <lfx-widget-menu
 *     [actions]="cardActions()"
 *     position="top-right"
 *     (actionClicked)="handleAction($event)">
 *   </lfx-widget-menu>
 *   <!-- Card content -->
 * </div>
 * ```
 * 
 * ### Component Setup
 * ```typescript
 * protected readonly cardActions = signal<WidgetAction[]>([
 *   { id: 'report', tooltip: 'Report issue', icon: 'fas fa-comment' },
 *   { id: 'share', tooltip: 'Share', icon: 'fas fa-share-alt' }
 * ]);
 * 
 * protected handleAction(event: WidgetActionClickEvent): void {
 *   console.log('Action clicked:', event.actionId);
 * }
 * ```
 * 
 * ### Parent CSS (Required)
 * ```scss
 * .card {
 *   position: relative;
 *   
 *   &:hover lfx-widget-menu {
 *     opacity: 1;
 *     visibility: visible;
 *   }
 * }
 * ```
 */
@Component({
  selector: 'lfx-widget-menu',
  imports: [CommonModule, HoverTriggerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './widget-menu.component.html',
  styleUrls: ['./widget-menu.component.scss']
})
export class WidgetMenuComponent {
  // =============================================================================
  // INPUTS
  // =============================================================================
  
  /** Array of action items to display in the menu */
  readonly actions = input.required<WidgetAction[]>();
  
  /** Position of the menu relative to parent component */
  readonly position = input<MenuPosition>('top-right');
  
  /** Force menu to be visible (useful for mobile override) */
  readonly visible = input<boolean>(false);
  
  // =============================================================================
  // OUTPUTS
  // =============================================================================
  
  /** Emitted when an action is clicked */
  readonly actionClicked = output<WidgetActionClickEvent>();
  
  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================
  
  /**
   * Handle action button click
   * 
   * Emits the action click event to parent component if the action is not disabled.
   * 
   * @param action - The action that was clicked
   */
  protected handleActionClick(action: WidgetAction): void {
    if (action.disabled) {
      return;
    }
    
    this.actionClicked.emit({
      action,
      actionId: action.id
    });
  }
  
  /**
   * Get computed CSS classes for the menu container
   * 
   * @returns CSS class string for the menu
   */
  protected getMenuClasses(): string {
    const classes = ['widget-menu'];
    
    // Add position class
    classes.push(`widget-menu--${this.position()}`);
    
    // Add visible class if forced visible
    if (this.visible()) {
      classes.push('widget-menu--visible');
    }
    
    return classes.join(' ');
  }
  
  /**
   * Check if action has a FontAwesome icon
   * 
   * @param action - The action to check
   * @returns True if action has FontAwesome icon
   */
  protected hasIcon(action: WidgetAction): boolean {
    return !!action.icon;
  }
  
  /**
   * Check if action has an SVG icon
   * 
   * @param action - The action to check
   * @returns True if action has SVG icon
   */
  protected hasSvgIcon(action: WidgetAction): boolean {
    return !!action.svgIcon;
  }
}

